import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import moment from "moment";

import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Button, Pager, useAlert, useSimpleTableDataSource } from "capitalrx-components-2.0/lib/general";
import { Title } from "capitalrx-components-2.0/lib/typography";

import { financeGetProviderPaymentsList, getProviderManagementAPI } from "@/api";
import { Finance, ProviderManagement } from "@/api/interfaces";
import { AuthenticationContext } from "@/context";
import { BackLink } from "@/components";
import { PaymentDropdown } from "./PaymentDropdown";
import { Filter } from "./Filter";


type DataRow<T> = {
  id: number | string;
  data: T;
};

type SectionRow = {
  id: string;
  label: string;
  isUpcoming: boolean;
  payment_date?: string;
  result_rows: ReadonlyArray<DataRow<Finance.MedicalClaimProviderReportList>>;
  total_results: number;
  transaction?: Finance.MedicalPaymentTransactionSchema[];
};


function resolveProviderIds(provider?: ProviderManagement.ProviderSchema) {
  if (!provider) return { npi: null, tin: null };

  const npi = provider.billing_npi;
  const tin = provider.tax_id_number;

  return { npi, tin };
}

// Build outgoing request for backend:
function buildProviderPaymentsPayload(params: {
  provider?: ProviderManagement.ProviderSchema;
  pageNumber: number;
  resultsPerPage: number;
  filters: Record<string, any>;
}) {
  const { provider, pageNumber, resultsPerPage, filters } = params;
  const { npi, tin } = resolveProviderIds(provider);

  if (!npi || !tin) {
    throw new Error("Provider missing NPI/TIN in ProviderSchema");
  }

  const body: Record<string, any> = {
    billing_provider_npi: String(npi),
    billing_provider_tin: String(tin),
    page_number: pageNumber + 1,
    results_per_page: resultsPerPage,
  };

  if (filters.external_member_id) body.external_member_id = filters.external_member_id;
  if (filters.patient_name) body.patient_name = filters.patient_name;

  if (filters.claim_status) {
    body.claim_status = Array.isArray(filters.claim_status)
      ? filters.claim_status[0]
      : filters.claim_status;
  }

  // Date ranges
  if (filters.start_date_from)
    body.start_date_from = filters.start_date_from;
  if (filters.start_date_to)
    body.start_date_to = filters.start_date_to;
  if (filters.submission_date_from)
    body.submission_date_from = filters.submission_date_from;
  if (filters.submission_date_to)
    body.submission_date_to = filters.submission_date_to;
  if (filters.issued_date_from)
    body.issued_date_from = filters.issued_date_from;
  if (filters.issued_date_to)
    body.issued_date_to = filters.issued_date_to;

  // Boolean flags
  if (typeof filters.payment_is_void === "boolean")
    body.payment_is_void = filters.payment_is_void;

  if (typeof filters.payment_is_cleared === "boolean")
    body.payment_is_cleared = filters.payment_is_cleared;

  // Sorting
  if (filters.sort && filters.sort.length > 0) {
    body.order_by = filters.sort.map((s: any) => ({
      [s.field]: s.direction,
    }));
  }

  return body;
}


export const MedicalClaimsProviderList: FC = () => {
  const { getAuthToken } = useContext(AuthenticationContext);
  const showAlert = useAlert();

  const [provider, setProvider] = useState<ProviderManagement.ProviderSchema>();
  const [loadingProvider, setLoadingProvider] = useState(false);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  const [upcoming, setUpcoming] = useState<Finance.MedicalClaimProviderReportList[]>([]);
  const [groupedByDate, setGroupedByDate] = useState<
    Record<string, Finance.MedicalClaimProviderReportList[]>
  >({});
  const [transactionsByDate, setTransactionsByDate] = useState<
    Record<string, Finance.MedicalPaymentTransactionSchema[]>
  >({});

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const RESULTS_PER_PAGE = 10;

  const getProvider = useCallback(async () => {
    setLoadingProvider(true);
    try {
      const pmResponse = await getProviderManagementAPI(getAuthToken());
      setProvider(pmResponse.provider);
    } catch (error) {
      console.error("ProviderManagement fetch error", error);
    } finally {
      setLoadingProvider(false);
    }
  }, [getAuthToken]);

  useEffect(() => {
    getProvider();
  }, [getProvider]);

  useEffect(() => {
    setPage(0);
  }, [provider]);

  const makePaymentDateKey = useCallback((backendDate?: string): string => {
    if (!backendDate) return "Unscheduled";
    const m = moment.utc(backendDate, ["YYYY-MM-DD HH:mm:ss", moment.ISO_8601], true);
    if (!m.isValid()) return "Unscheduled";
    return m.format("YYYY-MM-DD");
  }, []);

  const fetchClaims = useCallback(
    async (pageNumber: number) => {
      setLoading(true);
      try {
        if (provider && !loadingProvider) {
          const payload = buildProviderPaymentsPayload({
              provider,
              pageNumber,
              resultsPerPage: RESULTS_PER_PAGE,
              filters: activeFilters,
          });

          const response: Finance.MedicalProviderPaymentsListResultSchema =
            await financeGetProviderPaymentsList(payload, getAuthToken());

          setUpcoming(response.results.upcoming_medical_claims_results ?? []);

          const transactions = (response.results.medical_payment_transactions ?? [])
            .map((t) => t.medical_payment_transaction)
            .filter(Boolean) as Finance.MedicalPaymentTransactionSchema[];

          const grouped: Record<string, Finance.MedicalClaimProviderReportList[]> = {};
          const txByDate: Record<string, Finance.MedicalPaymentTransactionSchema[]> = {};

          for (const tx of transactions) {
            const key = makePaymentDateKey(tx.settlement_date);
            const claims = tx.medical_claim_payment_results ?? [];

            if (!grouped[key]) grouped[key] = [];
            grouped[key] = grouped[key].concat(claims);

            if (!txByDate[key]) txByDate[key] = [];
            txByDate[key].push(tx);
          }

          setGroupedByDate(grouped);
          setTransactionsByDate(txByDate);
        }
      } catch (error) {
        console.error("Failed to fetch claims:", error);
        showAlert({
          type: "error",
          style: "banner",
          message: "Failed to fetch claims",
        });
      } finally {
        setLoading(false);
      }
    },
    [
      RESULTS_PER_PAGE,
      provider,
      loadingProvider,
      getAuthToken,
      makePaymentDateKey,
      activeFilters,
    ],
  );

  useEffect(() => {
    fetchClaims(page);
  }, [fetchClaims, page]);

  const sectionRows: ReadonlyArray<DataRow<SectionRow>> = useMemo(() => {
    const rows: DataRow<SectionRow>[] = [];

    if (upcoming.length > 0) {
      const upcomingRows: ReadonlyArray<DataRow<Finance.MedicalClaimProviderReportList>> =
        upcoming.map((item, index) => ({ id: `upcoming-${index}`, data: item }));

      rows.push({
        id: "upcoming",
        data: {
          id: "upcoming",
          label: "Upcoming",
          isUpcoming: true,
          payment_date: undefined,
          result_rows: upcomingRows,
          total_results: upcomingRows.length,
          transaction: undefined,
        },
      });
    }

    const issuedKeys = Object.keys(groupedByDate).sort((a, b) => {
      if (a === "Unscheduled") return 1;
      if (b === "Unscheduled") return -1;
      return moment(b, "YYYY-MM-DD").valueOf() - moment(a, "YYYY-MM-DD").valueOf();
    });

    for (const dateKey of issuedKeys) {
      const claimsForDate = groupedByDate[dateKey] ?? [];
      const resultRows: DataRow<Finance.MedicalClaimProviderReportList>[] = claimsForDate.map(
        (item, idx) => ({ id: `${dateKey}-${idx}`, data: item }),
      );

      const displayDate =
        dateKey === "Unscheduled" ? undefined : moment.utc(dateKey, "YYYY-MM-DD").format("MM/DD/YYYY");

      const txForDate = transactionsByDate[dateKey] ?? [];

      rows.push({
        id: dateKey,
        data: {
          id: dateKey,
          label: displayDate ?? "Unscheduled",
          isUpcoming: false,
          payment_date: displayDate,
          result_rows: resultRows,
          total_results: resultRows.length,
          transaction: txForDate,
        },
      });
    }

    return rows;
  }, [upcoming, groupedByDate, transactionsByDate]);

  const sectionsDataSource = useSimpleTableDataSource<SectionRow>(sectionRows, { pageSize: 10 });

  useEffect(() => {
    sectionsDataSource.setFilter({ ...activeFilters });
  }, [activeFilters]);

  const handleFilterClick = () => setIsDrawerOpen(true);
  const handleClickCancel = () => setIsDrawerOpen(false);
  const handleApplyFilters = (filters: Record<string, any>) => setActiveFilters(filters);

  return (
    <Stack spacing="large">
      <Stack spacing="small">
        <BackLink linkText="Home" />
        <Shelf>
          <Title>Medical Claims & Payments</Title>
          <Shelf.Spacer />
          <Button
            appearance="secondary"
            icon="filter"
            onClick={handleFilterClick}
            disabled={sectionsDataSource.busy}
          >
            {`Filter (${filterCount})`}
          </Button>

          <Filter
            isOpen={isDrawerOpen}
            handleCancel={handleClickCancel}
            onFilterCountChange={setFilterCount}
            onApplyFilters={handleApplyFilters}
          />
        </Shelf>
      </Stack>

      <Stack spacing="medium-large">
        {sectionsDataSource.rows.length > 0 ? (
          sectionsDataSource.rows.map((row) => {
            const s = row.data;
            return (
              <PaymentDropdown
                key={s.id}
                isUpcoming={s.isUpcoming}
                payment_date={s.payment_date}
                result_rows={s.result_rows}
                total_results={s.total_results}
                transaction={s.transaction}
                loading={loading}
              />
            );
          })
        ) : (
          !loading && (
            <div style={{ padding: "8px 0" }}>No claims or payments found for this provider.</div>
          )
        )}

        {sectionRows.length > 0 && <Pager dataSource={sectionsDataSource} />}
      </Stack>
    </Stack>
  );
};
